/**
 * Pure composers for the FogBugz case → ADO Bug tool (epic PI-SPEC-SW-E311,
 * ER-1666 Package E, contract fogbugz-to-ado 1).
 *
 * Used by both the SpecTree web page (preview) and the SpecTree API (the
 * write), so the PM creates exactly the Bug and posts exactly the notes they
 * reviewed:
 *
 * - {@link composeFogbugzToAdoBug}: the ADO Bug's title, Repro Steps HTML,
 *   System Info HTML and tag. Every drafted string is HTML-escaped (drafts are
 *   untrusted model output and case text is written by others); newlines
 *   become `<br>`; empty sections are left out. Expected and actual live
 *   inside Repro Steps because the ADO Bug template has no fields of their
 *   own.
 * - {@link composeReporterQuestionsNote}: the plain-text FogBugz note asking
 *   the reporter for what is missing (below the bar, nothing written to ADO).
 * - {@link composeBugCreatedNote}: the plain-text FogBugz note "ADO Bug #N
 *   created by <PM>" with what the PM added, if anything (Resolved Decision 8).
 * - {@link composeFormulatorHandoff}: the plain-text description a Feature
 *   case hands to the ADO Formulator's start form (owner decision
 *   2026-10-29), at most `FORMULATOR_DESCRIPTION_MAX` characters.
 *
 * No I/O, no clock, no randomness: the same input always gives the same
 * output.
 */
import { FOGBUGZ_EVTX_PROVENANCE } from "../fogbugz/constants.js";
import { FORMULATOR_DESCRIPTION_MAX } from "../formulator/constants.js";
import { FOGBUGZ_TO_ADO_SLOT_LABELS, FOGBUGZ_TO_ADO_TAG } from "./constants.js";
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** `text` escaped for HTML element content and attribute values. */
function escapeHtml(text) {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
/** `text` trimmed, escaped, with line breaks (CRLF, LF or CR) as `<br>`. */
function escapeMultiline(text) {
    return escapeHtml(text.trim()).replace(/\r\n|\r|\n/g, "<br>");
}
/** `text` as one line: control characters and line breaks become single spaces. */
function oneLine(text) {
    return (text
        // eslint-disable-next-line no-control-regex -- control characters are exactly what is removed
        .replace(/[\u0000-\u001f\u007f\u2028\u2029]+/g, " ")
        .replace(/ {2,}/g, " ")
        .trim());
}
/** True when `url` is an absolute http(s) URL, so it may become a link. */
function isHttpUrl(url) {
    return /^https?:\/\/[^\s]+$/i.test(url.trim());
}
/** `<h3>heading</h3><p>text</p>`, or nothing when `text` is blank. */
function textSection(heading, text) {
    return text.trim() === "" ? "" : `<h3>${heading}</h3><p>${escapeMultiline(text)}</p>`;
}
/**
 * Compose the ADO Bug from a reviewed draft.
 *
 * Repro Steps:
 *
 * ```html
 * <h3>Steps to reproduce</h3><ol><li>…</li></ol>
 * <h3>Expected</h3><p>…</p>
 * <h3>Actual</h3><p>…</p>
 * <h3>Screenshots</h3><p><b>fileName</b>: description</p>…
 * <h3>Case history (digest)</h3><p>…</p>
 * <p>Brought from FogBugz case <a href="caseUrl">caseId</a> (fogbugz-<caseId>) by <pmName> with SpecTree Toolbox. Original files are attached.</p>
 * ```
 *
 * System Info: `<h3>Environment</h3><p>…</p>` and `<h3>Event logs</h3>` with
 * one `<h4>fileName</h4>` section per summary, each ending with the
 * provenance line "Written by <pmName>'s Claude from the parsed digest;
 * verify against the attached .evtx.".
 */
export function composeFogbugzToAdoBug(input) {
    const { draft, caseId, caseUrl, pmName } = input;
    const tag = FOGBUGZ_TO_ADO_TAG(caseId);
    const pm = escapeHtml(oneLine(pmName));
    const steps = draft.reproSteps.map((step) => step.trim()).filter((step) => step !== "");
    const screenshots = draft.screenshotDescriptions.filter((shot) => shot.description.trim() !== "");
    const caseLink = isHttpUrl(caseUrl)
        ? `<a href="${escapeHtml(caseUrl.trim())}">${String(caseId)}</a>`
        : `${String(caseId)} (${escapeHtml(oneLine(caseUrl))})`;
    const repro = [];
    if (steps.length > 0) {
        repro.push(`<h3>Steps to reproduce</h3><ol>${steps.map((step) => `<li>${escapeMultiline(step)}</li>`).join("")}</ol>`);
    }
    repro.push(textSection("Expected", draft.expected));
    repro.push(textSection("Actual", draft.actual));
    if (screenshots.length > 0) {
        repro.push(`<h3>Screenshots</h3>${screenshots
            .map((shot) => `<p><b>${escapeHtml(oneLine(shot.fileName))}</b>: ${escapeMultiline(shot.description)}</p>`)
            .join("")}`);
    }
    repro.push(textSection("Case history (digest)", draft.historyDigest));
    repro.push(`<p>Brought from FogBugz case ${caseLink} (${tag}) by ${pm} with SpecTree Toolbox. Original files are attached.</p>`);
    const systemInfo = [textSection("Environment", draft.environment)];
    const summaries = draft.evtxSummaries.filter((summary) => summary.text.trim() !== "");
    if (summaries.length > 0) {
        const provenance = escapeHtml(FOGBUGZ_EVTX_PROVENANCE(oneLine(pmName)));
        systemInfo.push(`<h3>Event logs</h3>${summaries
            .map((summary) => `<h4>${escapeHtml(oneLine(summary.fileName))}</h4><p>${escapeMultiline(summary.text)}</p><p><i>${provenance}</i></p>`)
            .join("")}`);
    }
    return {
        title: oneLine(draft.title),
        reproStepsHtml: repro.filter((part) => part !== "").join(""),
        systemInfoHtml: systemInfo.filter((part) => part !== "").join(""),
        tags: [tag],
    };
}
/**
 * The FogBugz note for a below-bar case: what is missing in plain words and
 * the numbered questions. Plain text (FogBugz event text is read raw).
 *
 * ```
 * <pmName> would like to bring this case to the ADO board as a Bug, but it is missing some details.
 *
 * Missing:
 * - Environment
 * - Severity
 *
 * Questions:
 * 1. Which version of Lynx were you using?
 * 2. …
 *
 * Please answer in this case.
 * ```
 */
export function composeReporterQuestionsNote(input) {
    const lines = [
        `${oneLine(input.pmName)} would like to bring this case to the ADO board as a Bug, but it is missing some details.`,
    ];
    const missing = [...new Set(input.missing)];
    if (missing.length > 0) {
        lines.push("", "Missing:", ...missing.map((slot) => `- ${FOGBUGZ_TO_ADO_SLOT_LABELS[slot]}`));
    }
    if (input.questions.length > 0) {
        lines.push("", "Questions:", ...input.questions.map((question, index) => `${String(index + 1)}. ${oneLine(question.question)}`));
    }
    lines.push("", "Please answer in this case.");
    return lines.join("\n");
}
/**
 * The FogBugz note after create: `ADO Bug #<id> created by <pmName>: <url>`
 * and, when the PM filled missing slots, `<pmName> added:` with one
 * `<slot label>: <text>` line per addition, the text verbatim. Plain text;
 * case status and owner are never changed.
 */
export function composeBugCreatedNote(input) {
    const pm = oneLine(input.pmName);
    const lines = [`ADO Bug #${String(input.adoId)} created by ${pm}: ${oneLine(input.adoUrl)}`];
    if (input.pmAdditions.length > 0) {
        lines.push("", `${pm} added:`, ...input.pmAdditions.map((addition) => `${FOGBUGZ_TO_ADO_SLOT_LABELS[addition.slot]}: ${addition.text}`));
    }
    return lines.join("\n");
}
/** The ellipsis that ends shortened opening text. */
const ELLIPSIS = "…";
/** `text` cut to at most `max` UTF-16 units without splitting a surrogate pair. */
function cutTo(text, max) {
    if (text.length <= max)
        return text;
    let cut = text.slice(0, Math.max(0, max));
    const last = cut.charCodeAt(cut.length - 1);
    if (last >= 0xd800 && last <= 0xdbff)
        cut = cut.slice(0, -1);
    return cut;
}
/**
 * The plain-text description a Feature case hands to the ADO Formulator's
 * create start (a User Story; the PM still clicks Start):
 *
 * ```
 * <title>
 *
 * <openingText>
 *
 * From FogBugz case <caseId>: <caseUrl>
 * ```
 *
 * At most `FORMULATOR_DESCRIPTION_MAX` characters. When too long, the opening
 * text is shortened and ends with "…"; the title line and the FogBugz line
 * are never cut (only a title that alone would not fit beside the FogBugz
 * line is shortened, as a last resort). Blank opening text is left out.
 */
export function composeFormulatorHandoff(input) {
    const fogbugzLine = `From FogBugz case ${String(input.caseId)}: ${oneLine(input.caseUrl)}`;
    const separator = "\n\n";
    let title = oneLine(input.title);
    const titleRoom = FORMULATOR_DESCRIPTION_MAX - fogbugzLine.length - separator.length;
    if (title.length > titleRoom)
        title = `${cutTo(title, titleRoom - ELLIPSIS.length).trimEnd()}${ELLIPSIS}`;
    const opening = input.openingText.trim();
    const frame = title.length + fogbugzLine.length + 2 * separator.length;
    if (opening === "" || frame >= FORMULATOR_DESCRIPTION_MAX) {
        return `${title}${separator}${fogbugzLine}`;
    }
    const room = FORMULATOR_DESCRIPTION_MAX - frame;
    const body = opening.length <= room ? opening : `${cutTo(opening, room - ELLIPSIS.length).trimEnd()}${ELLIPSIS}`;
    return `${title}${separator}${body}${separator}${fogbugzLine}`;
}
//# sourceMappingURL=compose.js.map